import { Else, If, Then } from 'react-if';
import { useModal } from '@ebay/nice-modal-react';
import cns from 'classnames';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { useDialog } from '@zep/dialog';
import { LabelStatus, PlanStatus } from '@zep/types';
import { Button, Stack } from '@zep/ui';
import { CreditCardIcon } from '../../../icons';
import { paymentFailureFromError } from '../../payment-tracking/paymentFailure';
import { createPaymentFailureDialog, PaymentFailureContent } from '../../payment-tracking/PaymentFailureContent';
import { readPaymentError } from '../paymentError';
import { RegisterPaymentCardModal } from '../paymentForm/PaymentCardModal';
import { usePlanSpaceDetailContext } from '../plan';
import { AdditionalLabel, PlanSpaceGroup } from '../space';
import S from './AppliedPlan.module.scss';
import { PaypleChangePaymentButton } from './PaypleChangePaymentButton';

// 서버가 내려주는 카드 등록 실패 에러 모양(status 코드 + data.message).

export const PaymentMethods = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    reload
  } = useRouter();
  const dialog = useDialog();
  const modal = useModal(RegisterPaymentCardModal);
  const {
    spacePlan,
    space,
    billingInfo,
    spaceId
  } = usePlanSpaceDetailContext();
  const isError = (spacePlan?.paymentFailCount ?? 0) > 0;
  const paymentFlow = billingInfo === undefined ? undefined : billingInfo === null ? 'card_registration' as const : 'card_change' as const;
  const showEditPaymentCardModal = async () => {
    logger.track('home_subscription_payment_method_change');
    await modal.show({
      spaceHashId: spacePlan?.spaceHashId ?? space.hashId,
      paymentFlow,
      onSuccess() {
        reload();
      },
      onError: async (error: unknown) => {
        const {
          status,
          message
        } = readPaymentError(error);
        const failureDialog = paymentFlow ? createPaymentFailureDialog(dialog, {
          payment_flow: paymentFlow,
          pg_provider: 'toss',
          space_id: spaceId,
          ...paymentFailureFromError(error)
        }, error) : dialog;
        if (status === -157) {
          await failureDialog.alert({
            title: t('subscription:payment.alertFailCardRegister.title'),
            content: t('subscription:SpaceDetail.changeCardModal.reason', {
              message: message ?? 'Processing Failure'
            })
          });
          return;
        }

        // FAIL_TO_CREATE_BILLING_KEY
        // FIXME: (주의) 서버의 error 객체의 depth가 error.data.message 일수 있고 error.message 일수 있음;;;
        if (status === -155) {
          await failureDialog.alert({
            title: t('subscription:payment.alertRegisterFail.title', '카드 등록 실패'),
            content: t('subscription:payment.alertRegisterFail.descriptionWithReason', {
              defaultValue: '사유: {{failReason}}',
              failReason: message || '카드 입력 정보를 다시 확인해주세요.'
            })
          });
          return;
        }
        await failureDialog.alert({
          title: t('subscription:payment.alertFailCardRegister.title'),
          content: t('subscription:SpaceDetail.changeCardModal.description')
        });
      }
    });
  };
  return <PlanSpaceGroup title={t('subscription:SpaceDetail.menuManage.billingCard')} data-sentry-element="PlanSpaceGroup" data-sentry-component="PaymentMethods" data-sentry-source-file="PaymentMethods.tsx">
      <Stack gap={8} data-sentry-element="Stack" data-sentry-source-file="PaymentMethods.tsx">
        <Stack direction={'row'} gap={8} style={{
        alignItems: 'center'
      }} data-sentry-element="Stack" data-sentry-source-file="PaymentMethods.tsx">
          <CreditCardIcon width={18} height={18} data-sentry-element="CreditCardIcon" data-sentry-source-file="PaymentMethods.tsx" />
          <label className={cns(S.label, S.gray)}>
            {billingInfo?.cardName}
            {billingInfo?.cardNumber && ` ( ${billingInfo?.cardNumber} )`}
          </label>
          {isError && <AdditionalLabel status={LabelStatus.FAIL} />}
        </Stack>
        <Stack gap={8} style={{
        alignItems: 'flex-start'
      }} data-sentry-element="Stack" data-sentry-source-file="PaymentMethods.tsx">
          {isError && <label className={S.label_error}>
              <PaymentFailureContent metadata={{
            payment_flow: 'recurring',
            pg_provider: 'unknown',
            space_id: spaceId
          }}>
                {t('subscription:SpaceDetail.menuManage.messageCardChange')}
              </PaymentFailureContent>
            </label>}
          <If condition={space.countryCode === 'KR'} data-sentry-element="If" data-sentry-source-file="PaymentMethods.tsx">
            <Then data-sentry-element="Then" data-sentry-source-file="PaymentMethods.tsx">
              <Button variant={'sub'} size={'xs'} onClick={showEditPaymentCardModal} data-sentry-element="Button" data-sentry-source-file="PaymentMethods.tsx">
                {t('subscription:SpaceDetail.menuManage.billingCardChangeButton')}
              </Button>
            </Then>
            <Else data-sentry-element="Else" data-sentry-source-file="PaymentMethods.tsx">
              <PaypleChangePaymentButton spaceId={spaceId} paymentFlow={paymentFlow} spaceHashId={spacePlan?.spaceHashId ?? space.hashId} isUpdated={spacePlan?.planType !== 'FREE' && spacePlan?.status === PlanStatus.ACTIVE} countryCode={space?.countryCode} data-sentry-element="PaypleChangePaymentButton" data-sentry-source-file="PaymentMethods.tsx">
                {t('subscription:SpaceDetail.menuManage.billingCardChangeButton')}
              </PaypleChangePaymentButton>
            </Else>
          </If>
        </Stack>
      </Stack>
    </PlanSpaceGroup>;
};